import type { IChainClient, Address } from '@shared/chain';
import type { IdentityService } from './IdentityService';
import type { IStorage } from '@/storage/IStorage';
import { bytesToBase64 } from '@/lib/format';
import { canonicalBytes } from '@/crypto/canonical';
import { createLogger } from '@/lib/logger';
import { useSocialStore } from '@/store/social';

const logger = createLogger('service:social');

function didToAddress(did: string): Address {
  return did.replace(/^did:dpb:/, '') as Address;
}
function addressToDid(addr: string): string {
  return `did:dpb:${addr}`;
}
const FOLLOW_KEY = 'meshlink.following';

export interface SocialServiceDeps {
  chain: IChainClient;
  identity: IdentityService;
  storage: IStorage;
}

export class SocialService {
  constructor(private readonly deps: SocialServiceDeps) {}

  async follow(did: string): Promise<void> {
    const keys = this.deps.identity.requireUnlocked();
    const follower = didToAddress(keys.did);
    const followee = didToAddress(did);
    const signature = bytesToBase64(
      this.deps.identity.sign(canonicalBytes({ action: 'follow', follower, followee })),
    );
    await this.deps.chain.follow({ action: 'follow', follower, followee, signature });
    const set = await this.localFollows();
    set.add(did);
    await this.saveFollows(set);
    useSocialStore.getState().addFollowing(did);
  }

  async unfollow(did: string): Promise<void> {
    const keys = this.deps.identity.requireUnlocked();
    const follower = didToAddress(keys.did);
    const followee = didToAddress(did);
    const signature = bytesToBase64(
      this.deps.identity.sign(canonicalBytes({ action: 'unfollow', follower, followee })),
    );
    await this.deps.chain.unfollow({ action: 'unfollow', follower, followee, signature });
    const set = await this.localFollows();
    set.delete(did);
    await this.saveFollows(set);
    useSocialStore.getState().removeFollowing(did);
  }

  async getFollowing(): Promise<string[]> {
    const keys = this.deps.identity.requireUnlocked();
    const follower = didToAddress(keys.did);
    let remote: Address[] = [];
    try {
      remote = await this.deps.chain.getFollowing(follower);
    } catch (err) {
      logger.warn('获取关注列表失败，回退本地', { err });
    }
    const remoteDids = remote.map(addressToDid);
    const local = await this.localFollows();
    const merged = new Set<string>([...remoteDids, ...local]);
    const list = [...merged];
    useSocialStore.getState().setFollowing(list);
    return list;
  }

  async getFollowerCount(did: string): Promise<number> {
    try {
      const n = await this.deps.chain.getFollowerCount(didToAddress(did));
      useSocialStore.getState().setFollowerCount(did, n);
      return n;
    } catch {
      return 0;
    }
  }

  private async localFollows(): Promise<Set<string>> {
    const raw = await this.deps.storage.settings.get(FOLLOW_KEY);
    return new Set(Array.isArray(raw) ? (raw as string[]) : []);
  }

  private async saveFollows(set: Set<string>): Promise<void> {
    await this.deps.storage.settings.set(FOLLOW_KEY, [...set]);
  }
}

export default SocialService;
